import EmailSignupForm from "@/components/EmailSignupForm";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
    >
      {/* Texture overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.08] pointer-events-none mix-blend-multiply"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1602536052359-ef94c21c5948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGVsZWdhbnQlMjBhcnQlMjB0ZXh0dXJlfGVufDB8fHx8MTc4MTE1ODg0OXww&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Floating celestial mark */}
      <div aria-hidden="true" className="hidden md:block absolute top-32 right-[6%] opacity-30">
        <Celestial />
      </div>

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-20 md:pb-32 relative">
        <div className="grid grid-cols-12 gap-6">
          {/* Vertical label */}
          <div className="hidden md:flex col-span-1 items-start justify-center pt-2">
            <span className="vertical-label">A Quiet Invitation</span>
          </div>

          {/* Main hero copy */}
          <div className="col-span-12 md:col-span-8" data-reveal>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#1A1A1A]" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-[#5C5C5C] font-body">
                The Awakened Artist Academy
              </span>
            </div>

            {/* Free PDF badge — makes the offer unmistakable */}
            <div
              data-testid="hero-free-badge"
              className="inline-flex items-center gap-3 bg-[#1FA5B8] text-[#F7F5F2] px-4 py-2 mb-6"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#F7F5F2] animate-pulse" />
              <span className="font-body text-[11px] uppercase tracking-[0.24em]">
                Free PDF guide  ·  Sent to your inbox
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-[#1A1A1A] tracking-tighter leading-[0.95] text-[clamp(3rem,8vw,6.5rem)] font-medium"
            >
              <span className="italic font-light">Reawaken</span> your
              creativity.
            </h1>

            <p className="mt-6 font-display italic text-[#1FA5B8] text-[clamp(1.4rem,2.6vw,2rem)] leading-tight max-w-2xl">
              A free 5-minute guide for artists who&apos;ve been listening less.
            </p>

            <div className="mt-8 max-w-2xl font-body text-[#1A1A1A] text-lg md:text-xl leading-[1.75] space-y-5">
              <p>
                Enter your email below and I&apos;ll send you{" "}
                <span className="font-medium">The 5-Minute Creative Reactivation Formula</span>{" "}
                — a small editorial PDF that opens a doorway back to the work
                you keep almost making.
              </p>
              <p className="text-[#5C5C5C]">
                This is not a course. Not a system. Not another thing to do
                before you can begin. Just a quiet remembering. Free, and
                quietly delivered.
              </p>
            </div>

            <div id="signup" className="mt-12 max-w-xl">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#1FA5B8] font-body mb-2">
                Get the free guide  ·  Instant download
              </div>
              <p className="font-body text-sm text-[#5C5C5C] mb-5 leading-relaxed">
                7 pages. Editorial. Printable. Yours in the next minute.
              </p>
              <EmailSignupForm source="hero" testIdPrefix="hero" redirectTo="/the-deeper-reactivation" />
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[#5C5C5C] font-body text-sm">
              <span data-testid="hero-trust-1" className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-[#1FA5B8]" />
                A small first step
              </span>
              <span data-testid="hero-trust-2" className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-[#1FA5B8]" />
                Free, and quietly delivered
              </span>
              <span data-testid="hero-trust-3" className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-[#1FA5B8]" />
                Unsubscribe in one breath
              </span>
            </div>
          </div>

          {/* PDF preview card — what you actually get */}
          <aside
            className="col-span-12 md:col-span-3 md:col-start-10 mt-12 md:mt-2 self-start relative"
            data-testid="hero-free-pdf-preview"
            data-reveal
          >
            <a
              href="#signup"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("signup")?.scrollIntoView({ behavior: "smooth", block: "center" });
                setTimeout(() => document.querySelector('[data-testid="hero-signup-email"]')?.focus(), 500);
              }}
              className="block group"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#1FA5B8] font-body mb-3">
                What you&apos;ll receive
              </div>
              <div className="relative border border-[#E5E0D8] bg-[#FFFFFF] shadow-[0_20px_60px_-20px_rgba(26,26,26,0.35)] group-hover:shadow-[0_28px_80px_-24px_rgba(31,165,184,0.35)] transition-shadow">
                {/* Free ribbon */}
                <div className="absolute -top-3 -right-3 bg-[#1FA5B8] text-[#F7F5F2] px-3 py-1.5 z-10">
                  <span className="font-body text-[10px] uppercase tracking-[0.22em]">Free</span>
                </div>
                <img
                  src="/covers/cover_free.png"
                  alt="The 5-Minute Creative Reactivation Formula — free PDF guide cover"
                  className="w-full block"
                  loading="eager"
                />
              </div>
              <div className="mt-4 font-display text-[#1A1A1A] text-[20px] leading-[1.25] tracking-tight">
                The 5-Minute Creative <span className="italic font-light">Reactivation</span> Formula
              </div>
              <div className="mt-2 font-body italic text-[#5C5C5C] text-sm">
                A small shift to begin again.
              </div>
              <div className="mt-4 hairline" />
              <ul className="mt-4 space-y-2 font-body text-[12px] text-[#5C5C5C]">
                <li className="flex items-start gap-2">
                  <span className="text-[#1FA5B8]">·</span>
                  7 editorial pages, printable
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1FA5B8]">·</span>
                  6 gentle reflection questions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#1FA5B8]">·</span>
                  Delivered instantly, by email
                </li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-2 font-body text-[11px] uppercase tracking-[0.22em] text-[#1FA5B8] group-hover:gap-3 transition-all">
                Enter your email to get it
                <span className="text-[#1FA5B8]">→</span>
              </div>
            </a>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Celestial({ small = false }) {
  const size = small ? 22 : 64;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="32" cy="32" r="1.4" fill="#1FA5B8" />
      <circle cx="32" cy="32" r="12" stroke="#1FA5B8" strokeWidth="0.6" />
      <circle cx="32" cy="32" r="22" stroke="#1FA5B8" strokeWidth="0.4" strokeDasharray="1.4 3" />
      {[...Array(12)].map((_, i) => {
        const a = (i * 30 * Math.PI) / 180;
        const r1 = 26;
        const r2 = 30;
        const x1 = 32 + Math.cos(a) * r1;
        const y1 = 32 + Math.sin(a) * r1;
        const x2 = 32 + Math.cos(a) * r2;
        const y2 = 32 + Math.sin(a) * r2;
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#1FA5B8" strokeWidth="0.6" />
        );
      })}
    </svg>
  );
}
